import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Header() {
  return (
    <header className="px-6 sm:px-10 lg:px-16 pt-10 pb-8 relative z-10">
      <div className="max-w-5xl mx-auto flex items-baseline justify-between gap-6">
        <div>
          <div className="citation" data-testid="brand-tagline">
            A search instrument for the writings of
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-1"
            style={{ color: "var(--text-primary)" }}
            data-testid="brand-title"
          >
            Walt&nbsp;Whitman
          </h1>
        </div>
        <a
          href="https://whitmanarchive.org/"
          target="_blank"
          rel="noopener noreferrer"
          className="source-link hidden sm:inline-block"
          data-testid="header-archive-link"
        >
          Whitman Archive ↗
        </a>
      </div>
    </header>
  );
}

function SourceShelf({ sources }) {
  const ok = sources.filter((s) => s.text_len > 0);
  const failed = sources.filter((s) => s.text_len === 0);
  return (
    <details
      className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 mt-12 mb-4 relative z-10"
      data-testid="source-shelf"
    >
      <summary
        className="citation cursor-pointer select-none"
        data-testid="source-shelf-toggle"
      >
        {ok.length} sources indexed
        {failed.length > 0 ? `  ·  ${failed.length} unavailable` : ""}  ·  show catalog
      </summary>
      <ul className="mt-4 space-y-2 citation" data-testid="source-list">
        {sources.map((s) => (
          <li key={s.key} className="flex gap-3 items-baseline">
            <span
              className={
                "inline-block w-2 h-2 rounded-full " +
                (s.text_len > 0 ? "" : "")
              }
              style={{
                background:
                  s.text_len > 0 ? "var(--accent)" : "var(--border)",
              }}
            />
            <span className="flex-1">
              <span style={{ color: "var(--text-primary)" }}>{s.citation}</span>
              <span className="ml-2 opacity-60">
                {s.text_len > 0
                  ? `${Math.round(s.text_len / 1000).toLocaleString()}k chars`
                  : "unavailable"}
              </span>
            </span>
            <a
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="source-link"
            >
              open ↗
            </a>
          </li>
        ))}
      </ul>
    </details>
  );
}

function ResultItem({ match, query, index }) {
  const before = match.before;
  const after = match.after;
  const hasPage = Number.isFinite(match.page) && match.page > 0;
  return (
    <article
      className="fade-up py-10"
      style={{ animationDelay: `${index * 80}ms` }}
      data-testid={`result-item-${index}`}
    >
      <div className="citation mb-4" data-testid={`result-citation-${index}`}>
        {match.cited_as || match.citation}
      </div>

      <p
        className="font-body leading-loose text-lg sm:text-xl max-w-3xl"
        data-testid={`result-passage-${index}`}
      >
        <span className="context-text">{before ? before + " " : ""}</span>
        <span className="match-highlight" data-testid={`result-match-${index}`}>
          {match.match}
        </span>
        <span className="context-text">{after ? " " + after : ""}</span>
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2">
        {match.page_url && (
          <a
            href={match.page_url}
            target="_blank"
            rel="noopener noreferrer"
            className="source-link"
            data-testid={`result-page-link-${index}`}
          >
            open at p. {match.page} ↗
          </a>
        )}
        {hasPage && !match.page_url && (
          <span
            className="citation"
            style={{ color: "var(--text-secondary)" }}
            data-testid={`result-page-${index}`}
          >
            p. {match.page}
          </span>
        )}
        <span className="citation opacity-60">
          query: <span style={{ color: "var(--text-primary)" }}>"{query}"</span>
        </span>
      </div>
    </article>
  );
}

export default function App() {
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [results, setResults] = useState([]);
  const [fuzzy, setFuzzy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sources, setSources] = useState([]);

  const loadSources = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/sources`);
      setSources(r.data || []);
    } catch {
      // non-fatal: catalog will simply not render
    }
  }, []);

  useEffect(() => {
    // Catalog is populated at ingest time and does not change while the
    // app is running, so a single load on mount is sufficient. (We used
    // to poll every 15 s, which multiplied by every open tab and every
    // Kubernetes health probe was flooding /api/sources in production.)
    loadSources();
  }, [loadSources]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (q.length < 2) return;
    setLoading(true);
    setError("");
    setSubmitted(q);
    try {
      const r = await axios.get(`${API}/search`, {
        params: { q, per_source_limit: 5 },
      });
      setResults(r.data.matches || []);
      setFuzzy(!!r.data.fuzzy);
    } catch {
      setError("Search failed. Please try again.");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      <Header />

      <main className="relative z-10">
        <section className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 pt-6 pb-2">
          <form onSubmit={onSubmit} data-testid="search-form">
            <label
              htmlFor="q"
              className="citation block mb-3"
              data-testid="search-label"
            >
              Write the quotation you want to search on the line below
              without quotation marks. Click on the &ldquo;Find&rdquo; button,
              which will change to &ldquo;Searching.&rdquo; When the
              &ldquo;Searching&rdquo; button switches back to
              &ldquo;Find,&rdquo; scroll down to see the results. To see
              the original source in its context, click on the
              &ldquo;Open at&rdquo; link; ignore the page number given
              there.
            </label>
            <p
              className="citation block mb-3"
              data-testid="search-label-tips"
            >
              If you do not get satisfactory results, try shortening
              the search string (use fewer words). If the string contains a
              past-tense verb ending <em>ed</em>, try changing it to end in
              &ldquo;&rsquo;d&rdquo; (apostrophe d). Scroll past
              all the results to read more detailed help instructions.
            </p>
            <div className="flex items-end gap-4">
              <input
                id="q"
                className="search-input"
                placeholder="write the quotation you want to search here"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
                spellCheck={false}
                data-testid="search-input"
              />
              <button
                type="submit"
                className="btn-accent mb-1 whitespace-nowrap"
                disabled={loading || query.trim().length < 2}
                data-testid="search-submit"
              >
                {loading ? "searching." : "find"}
              </button>
            </div>
          </form>
        </section>

        {/* Results — placed directly under the search input so the first
            match's "Open at" link is visible without scrolling past the
            help block. */}
        <section
          className="max-w-3xl mx-auto px-6 sm:px-10 lg:px-16 mt-10 relative z-10"
          data-testid="results-section"
        >
          {error && (
            <p
              className="citation"
              style={{ color: "var(--accent)" }}
              data-testid="search-error"
            >
              {error}
            </p>
          )}

          {submitted && !loading && results.length === 0 && !error && (
            <div className="py-12 fade-up" data-testid="no-results">
              <p
                className="font-display italic text-2xl"
                style={{ color: "var(--text-secondary)" }}
              >
                No occurrences of “{submitted}” were found in the indexed
                sources.
              </p>
              <p className="citation mt-4">
                Try a shorter or less punctuated fragment, or check the source
                catalog below for ingestion status.
              </p>
            </div>
          )}

          {fuzzy && results.length > 0 && (
            <p
              className="citation mb-4 fade-up"
              style={{ color: "var(--accent)" }}
              data-testid="fuzzy-notice"
            >
              No exact match found — showing closest approximate matches.
            </p>
          )}

          <div data-testid="results-list">
            {results.map((m, i) => (
              <div key={`${m.source_key}-${m.char_offset}-${i}`}>
                {i > 0 && <div className="result-divider" />}
                <ResultItem match={m} query={submitted} index={i} />
              </div>
            ))}
          </div>

          {results.length > 0 && (
            <p
              className="citation py-6"
              data-testid="results-count"
              style={{ color: "var(--text-secondary)" }}
            >
              {results.length} match{results.length === 1 ? "" : "es"} ·
              query: <span style={{ color: "var(--text-primary)" }}>"{submitted}"</span>
            </p>
          )}
        </section>

        {/* Help / usage tips — kept below the results so they are always
            available but never push the results below the fold. */}
        <section className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 pt-2 pb-4">
          <div
            className="citation max-w-2xl space-y-3"
            style={{ color: "var(--text-secondary)" }}
            data-testid="search-help"
          >
            <p>
              Returns the full source citation with page number, 40 words
              before the match, and 40 words after. Matching is case- and
              punctuation-insensitive.
            </p>
            <p>
              <strong style={{ textTransform: "uppercase", color: "var(--text-primary)" }}>
                Help Instructions
              </strong>
            </p>
            <p>
              If the source is in Internet Archive, you can use
              that Archive&rsquo;s search engine to get to your source
              words more quickly. Be sure to put the search string inside
              quotation marks in the Archive&rsquo;s search engine. You
              can &ldquo;borrow&rdquo; from Internet Archive for free if
              you want to hold on to the source for any length of time.
            </p>
            <p>
              If you still don&rsquo;t get satisfactory results, click on
              &ldquo;Whitman Archive&rdquo; in the upper right corner of
              the Quote-Finder. That will take you to the Walt Whitman
              Archive, which has an excellent search engine (and is full
              of invaluable material by and about Whitman). In this
              search engine, you should type the words inside quotation
              marks. Again, if you don&rsquo;t get results, shorten the
              search string.
            </p>
            <p>
              Finally, if you still get no good results, type the string
              in quotation marks, along with the words <em>Walt Whitman</em>,
              into your favorite internet search engine.
            </p>
          </div>
        </section>

        <SourceShelf sources={sources} />

        <footer className="max-w-5xl mx-auto px-6 sm:px-10 lg:px-16 pb-16 pt-10 relative z-10">
          <p
            className="citation"
            style={{ color: "var(--text-secondary)" }}
            data-testid="footer-note"
          >
            Sources are indexed from public-domain transcriptions (archive.org,
            The Atlantic, Walt Whitman Quarterly Review, Penn English). The
            HathiTrust volumes the catalog cites correspond, page-for-page, to
            the 10-volume Complete Writings of Walt Whitman (1902) whose full
            OCR is mirrored at archive.org.
          </p>
        </footer>
      </main>
    </div>
  );
}
